import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import type { Exact } from "@Helpers/types/exact";
import type { StatusResponse, ObjectIdString } from "@Helpers/schemas/common.schema";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import type {
  SaveProjectsRequest,
  SaveProjectsResponse,
  SaveProjectStatusRequest,
  SaveProjectStatusResponse,
  SaveProjectAssignedPositionResponse,
  GetAllStatusesForProjectsResponse,
} from "@ServerTypes/projects/project.schema";
import type {
  SaveTaskStatusRequest,
  SaveTaskStatusResponse,
  DeleteTaskRequest,
  DeleteTaskResponse,
  GetAllStatusesForTasksResponse,
} from "@ServerTypes/tasks/tasks.schema";
import type {
  GetPdfMarkUpDataRequest,
  GetPdfMarkUpDataResponse,
  GetIssuesIdToExportRequest,
  GetIssuesIdToExportResponse,
  SaveIssuesDataFromBCFRequest,
  SaveIssuesDataFromBCFResponse,
  GetIssuesDataRequest,
  GetIssuesDataResponse,
  IssueItem,
  GetIssuesPinDataRequest,
  GetIssuesPinDataResponse,
  GetIssueViewpointDataRequest,
  GetIssueViewpointDataResponse,
  GetIssuesDataByPortalRequest,
  GetIssuesDataByPortalResponse,
  RemoveIssueFromFormRequest,
  RemoveIssueFromFormResponse,
  AddFormIdToIssuesRequest,
  AddFormIdToIssuesResponse,
  UpdateIssueCustomFieldsRequest,
  UpdateIssueCustomFieldsResponse,
} from "@ServerTypes/issues/issues.schema";
import type { AxiosResponse } from "axios";
import type { StatusItem } from "@ServerTypes/status/status.schema";
import type { FetchDefaultListOfStatusesAvailableResponse } from "@ServerTypes/reviews/reviews.schema";
import type {
  GetIssueCommentsRequest,
  GetCommentsResponse,
  SaveIssueCommentRequest,
  SaveCommentsResponse,
  DeleteCommentsRequest,
  DeleteCommentsResponse,
} from "@ServerTypes/comments/comments.schema.js";
import { defaultListOfStatusAvailableUrl } from "@Services/dataManager";
import { showToastNotification } from "@Reducers/toastSlice";
import { setIssuesCount } from "./issuesCount";
import { setTasksStatusesData } from "@Reducers/tasksStatuses";
import { setProjectsStatusesData } from "@Reducers/projectsStatuses";
import { setItemsHaveError, setItemsAreLoading } from "./bcfData";
import { setIssuesPinData } from "./issuesPinDataSlice";
import {
  updateIssuesData as arrangeIssuesData,
  setIssuesData,
  modifyIssuesData,
  setIssuesCommentsData,
  deleteIssuesCommentsData,
  mergeIssuesData as _mergeIssuesData,
  removeIssueFromState,
} from "./issues";
import { t, untranslated } from "@i18n";
import { EXPORT_TYPE, exportPdf } from "@Services/fileDownload";
import { errorCodes } from "@Helpers/errorCodes";

/** Row guard for the `unknown[]` the tasks-status endpoint declares; only `name` is required. */
function isStatusItem(value: unknown): value is StatusItem {
  return typeof value === "object" && value !== null && "name" in value && typeof value.name === "string";
}

interface ExportParams {
  projectId: ObjectIdString;
  issuesIds?: ObjectIdString[];
}

/**
 * Metadata-only update of an issue's standard/custom field answers from the detail sidebar.
 * The server validates only the isMetadata fields before persisting the edited formTemplate.
 */
export const updateIssueCustomFieldsRequest = (
  data: UpdateIssueCustomFieldsRequest,
): Promise<AxiosResponse<UpdateIssueCustomFieldsResponse>> =>
  axiosWrapper.post<UpdateIssueCustomFieldsResponse, UpdateIssueCustomFieldsRequest>(
    `${getBackendUrl()}/issues/updateIssueCustomFields`,
    data,
  );

export function removeIssueFromForm(
  ids: RemoveIssueFromFormRequest["ids"],
  formId: RemoveIssueFromFormRequest["formId"],
) {
  return async (dispatch: AppDispatch) => {
    const data: RemoveIssueFromFormRequest = { ids, formId };
    const response = await axiosWrapper.post<RemoveIssueFromFormResponse>(
      `${getBackendUrl()}/issues/removeIssueFromForm`,
      data,
    );
    if (response.data.status) {
      dispatch(removeIssueFromState({ ids }));
      dispatch(
        showToastNotification({
          content: t("All the selected Issue(s) were removed"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    return response.data.status;
  };
}

export function addFormIdToIssues(ids: AddFormIdToIssuesRequest["ids"], formId: AddFormIdToIssuesRequest["formId"]) {
  return async (dispatch: AppDispatch) => {
    const data: AddFormIdToIssuesRequest = { ids, formId };
    const response = await axiosWrapper.post<AddFormIdToIssuesResponse>(
      `${getBackendUrl()}/issues/addFormIdToIssues`,
      data,
    );
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("All the selected Issue(s) were added"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };
}

export function updateTaskData(data: Record<string, unknown>) {
  return {
    type: "UPDATE_TASK_DATA",
    data,
  };
}
export function removeTaskData(data: Record<string, unknown>) {
  return {
    type: "REMOVE_TASK_DATA",
    data,
  };
}
// `issueId: ObjectIdString` (not `Pick<…, "issueId">`) because the comments map in the issues slice
// is keyed by ONE issue id: the request schema's `ObjectIdString[]` arm has no reducer path here, so
// the single-id requirement belongs in this signature rather than in an assertion at the dispatch.
export function getCommentsData<T extends Exact<GetIssueCommentsRequest, T>>(data: T & { issueId: ObjectIdString }) {
  return async (dispatch: AppDispatch) => {
    dispatch(setItemsAreLoading(true));
    const responseData = await axiosWrapper.post<GetCommentsResponse>(
      `${getBackendUrl()}/comments/getIssueComments`,
      data,
    );
    if (responseData.data.status && responseData.data.data) {
      dispatch(
        setIssuesCommentsData({
          data: responseData.data.data,
          issueId: data.issueId,
        }),
      );
      return responseData.data.data;
    }
  };
}
export function saveCommentsData<T extends Exact<SaveIssueCommentRequest, T>>(
  data: T & Pick<SaveIssueCommentRequest, "issueId">,
) {
  return (dispatch: AppDispatch) => {
    dispatch(setItemsAreLoading(true));
    axiosWrapper
      .post<SaveCommentsResponse>(`${getBackendUrl()}/comments/saveIssueComment`, data)
      .then((response) => {
        if (response.data.status && response.data.data) {
          dispatch(
            setIssuesCommentsData({ data: response.data.data, issueId: data.issueId, isEdit: !!data.commentId }),
          );
        }
      })
      .catch((error) => {
        dispatch(setItemsHaveError(true));
        Promise.reject(error);
      });
  };
}
// `issueId` is deliberately NOT part of the request body: the server resolves the issue linkage
// (and the creator) from the STORED comment document, so sending either would be dead weight on the
// wire. The issues slice still needs to know which issue's comment map to prune, so the caller
// passes it alongside the payload as local state.
export function deleteCommentsData<T extends Exact<DeleteCommentsRequest, T>>(data: T, issueId: ObjectIdString) {
  return (dispatch: AppDispatch) => {
    dispatch(setItemsAreLoading(true));
    axiosWrapper
      .post<DeleteCommentsResponse>(`${getBackendUrl()}/comments/deleteCommentsData`, data)
      .then((response) => {
        if (response.data.status) {
          dispatch(deleteIssuesCommentsData({ commentId: data.commentId, issueId }));
        }
      })
      .catch((error) => {
        dispatch(setItemsHaveError(true));
        Promise.reject(error);
      });
  };
}
export const issuesInFilesCount = async <T extends Exact<GetIssuesDataRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetIssuesDataResponse>(`${getBackendUrl()}/issues/getIssuesData`, data);
  const result = response.data.result as unknown[] | undefined;
  return result?.length ?? 0;
};

export function getIssuesData<T extends Exact<GetIssuesDataRequest, T>>(
  data: T,
  returnValue = false,
  setIssueRequestState?: (state: { loading: boolean; error: null | number }) => void,
) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<GetIssuesDataResponse>(`${getBackendUrl()}/issues/getIssuesData`, data);
    if (response.data.status && response.data.result) {
      dispatch(setIssuesData(response.data.result));
      if (typeof response.data.count === "number") {
        dispatch(setIssuesCount(response.data.count));
      }
    }
    setIssueRequestState &&
      setIssueRequestState({
        loading: false,
        error: !response.data.status ? response.data.errorCode || errorCodes.SOMETHING_WENT_WRONG : null,
      });
    if (returnValue) {
      return response.data.result;
    }
  };
}

export function fetchIssueById(issueId: NonNullable<IssueItem["id"]>) {
  return async (dispatch: AppDispatch): Promise<IssueItem | undefined> => {
    const result = await dispatch(getIssuesData({ issueId }, true));
    if (!Array.isArray(result)) return undefined;
    return result.find((item) => item.id === issueId) ?? result[0];
  };
}

export async function getIssuesList<T extends Exact<GetIssuesDataRequest, T>>(
  data: T,
): Promise<Record<string, unknown>[]> {
  const response = await axiosWrapper.post<GetIssuesDataResponse>(`${getBackendUrl()}/issues/getIssuesData`, data);
  // `result` is absent on a failed read, which is what the `?? []` covers — the old assertion sat in
  // front of it and made the fallback look dead. The declared `Record<string, unknown>[]` is the
  // open shape the sole consumer (issuesPdf) re-narrows to its own row type.
  return response.data.result ?? [];
}
export function getIssueViewpointData<T extends Exact<GetIssueViewpointDataRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<GetIssueViewpointDataResponse>(
      `${getBackendUrl()}/issues/getIssueViewpointData`,
      data,
    );
    if (response.data.status && response.data.result) {
      dispatch(modifyIssuesData(response.data.result));
    }
  };
}
export function getfilteredIssuesData<T extends Exact<GetIssuesDataRequest, T>>(data: T, returnValue = false) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<GetIssuesDataResponse>(`${getBackendUrl()}/issues/getIssuesData`, data);
    if (response.data.status && response.data.result) {
      dispatch(arrangeIssuesData(response.data.result));
      if (typeof response.data.count === "number") {
        dispatch(setIssuesCount(response.data.count));
      }
    }
    if (returnValue) {
      return response.data.result;
    }
  };
}
export function getIssuesPinData<T extends Exact<GetIssuesPinDataRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<GetIssuesPinDataResponse>(
      `${getBackendUrl()}/issues/getIssuesPinData`,
      data,
    );
    if (response.data.status && response.data.result) {
      dispatch(setIssuesPinData(response.data.result));
    }
  };
}

export function getIssuesDataByPortal<T extends Exact<GetIssuesDataByPortalRequest, T>>(data: T) {
  return (dispatch: AppDispatch) => {
    dispatch(setItemsAreLoading(true));
    axiosWrapper
      .post<GetIssuesDataByPortalResponse>(`${getBackendUrl()}/issues/getIssuesDataByPortal`, data)
      .then((response) => {
        if (response.data.result) {
          dispatch(setIssuesData(response.data.result));
        }
      })
      .catch((error) => {
        dispatch(setItemsHaveError(true));
        Promise.reject(error);
      });
  };
}
export async function getCompleteIssuesDataByPortal<T extends Exact<GetIssuesDataByPortalRequest, T>>(data: T) {
  const response = await axiosWrapper.post<GetIssuesDataByPortalResponse>(
    `${getBackendUrl()}/issues/getIssuesDataByPortal`,
    data,
  );
  return response.data.result ?? [];
}
export function getTasksStatusesData() {
  return (dispatch: AppDispatch) => {
    dispatch(setItemsAreLoading(true));
    axiosWrapper
      .get<GetAllStatusesForTasksResponse>(`${getBackendUrl()}/tasks/getAllStatusesForTasks`)
      .then((response) => {
        if (response.data.status) {
          // `getAllStatusesForTasksResponse.data` is `unknown[]` (its only producer is a stub that
          // always answers `[]`), so each row is checked rather than the array named by assertion.
          dispatch(setTasksStatusesData((response.data.data ?? []).filter(isStatusItem)));
        }
      })
      .catch((error) => {
        dispatch(setItemsHaveError(true));
        Promise.reject(error);
      });
  };
}
export function getProjectsStatusesData() {
  return (dispatch: AppDispatch) => {
    dispatch(setItemsAreLoading(true));
    axiosWrapper
      .get<GetAllStatusesForProjectsResponse>(`${getBackendUrl()}/projects/getAllStatusesForProjects`)
      .then((response) => {
        if (response.data.status) {
          dispatch(setProjectsStatusesData(response.data.data));
        }
      })
      .catch((error) => {
        dispatch(setItemsHaveError(true));
        Promise.reject(error);
      });
  };
}
// KNOWN BUG: the success toast below is unconditional theatre — POST /projects/saveProjectStatus is a
// server-side no-op stub, so this thunk tells the user "Status changed successfully" for a change that
// was never written. The user sees the green toast, and the project's status is unchanged on the next
// load (the settings panel reads `statusId`:
// packages/web/src/features/projects/pages/ProjectSettings/GeneralProjectInformation/index.tsx:109).
// Evidence: the wired handler is packages/server/projects/project.service.ts:173 (saveProjectStatus), whose
// entire body is `return Promise.resolve({ status: true })` — no DB write, no permission gate — so
// `response.data.status` below is true for every request, including one with a bogus id or status.
// See the full annotation at that handler. The fix here would be to drop the toast (or this thunk),
// but that is not applied because it only becomes correct once the product decides whether the endpoint
// should persist a status or be retired in favour of /updateProjectsData (which does write `statusId`
// under EDIT_PROJECT_INFO). Note: as of 2026-09-02 no component dispatches this thunk — it is exported
// but unreferenced across packages/web and packages/mobile — so the misleading toast only reaches a
// user once someone wires this up; do that only after the server side actually writes.
export function saveProjectStatus<T extends Exact<SaveProjectStatusRequest, T>>(data: T) {
  return (dispatch: AppDispatch) => {
    axiosWrapper
      .post<SaveProjectStatusResponse>(`${getBackendUrl()}/projects/saveProjectStatus`, data)
      .then((response) => {
        if (response.data.status) {
          dispatch(
            showToastNotification({
              content: t("Status changed successfully"),
            }),
          );
        }
      })
      .catch((error) => {
        dispatch(setItemsHaveError(true));
        Promise.reject(error);
      });
  };
}
export function saveProjectAttachedPositionData(data: Record<string, unknown>) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<SaveProjectAssignedPositionResponse>(
      `${getBackendUrl()}/projects/saveProjectAssignedPosition`,
      data,
    );
    let toastFields;
    if (response.data.status) {
      toastFields = {
        content: t("Assigned position changed successfully"),
        type: TOAST_TYPE_SUCCESS,
      };
    } else {
      toastFields = {
        content: untranslated(response.data.errorMessage || SOMETHING_WENT_WRONG),
        type: TOAST_TYPE_ERROR,
      };
    }
    dispatch(showToastNotification(toastFields));
  };
}
export function saveTaskStatus<T extends Exact<SaveTaskStatusRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    await axiosWrapper
      .post<SaveTaskStatusResponse>(`${getBackendUrl()}/tasks/saveTaskStatus`, data)
      .then((response) => {
        if (response.data.status) {
          dispatch(updateTaskData(data));
        }
      })
      .catch((error) => {
        dispatch(setItemsHaveError(true));
        Promise.reject(error);
      });
  };
}
export async function deleteTask<T extends Exact<DeleteTaskRequest, T>>(data: T) {
  const response = await axiosWrapper.post<DeleteTaskResponse>(`${getBackendUrl()}/tasks/deleteTask`, data);
  return response;
}
export async function saveProjectData<T extends Exact<SaveProjectsRequest, T>>(data: T): Promise<SaveProjectsResponse> {
  const response = await axiosWrapper.post<SaveProjectsResponse>(`${getBackendUrl()}/projects/saveProjectsData`, data);
  return response.data;
}
export async function getPdfMarkUpData<T extends Exact<GetPdfMarkUpDataRequest, T>>(data: T) {
  const response = await axiosWrapper.post<GetPdfMarkUpDataResponse>(
    `${getBackendUrl()}/issues/getPdfMarkUpData`,
    data,
  );
  return response;
}
export function successReadNotificationToast(selectedNotificationsId: string[]) {
  return async (dispatch: AppDispatch) => {
    const suffix = selectedNotificationsId.length > 1 ? "s" : "";
    dispatch(
      showToastNotification({
        content: t(`Notification${suffix} successfully marked as read`),
        type: TOAST_TYPE_SUCCESS,
      }),
    );
  };
}
export async function getCommentsList<T extends Exact<GetIssueCommentsRequest, T>>(
  data: T,
): Promise<{ data: GetCommentsResponse }> {
  return await axiosWrapper.post<GetCommentsResponse>(`${getBackendUrl()}/comments/getIssueComments`, data);
}
async function getIssuesIdToExport(data: GetIssuesIdToExportRequest) {
  return await axiosWrapper.post<GetIssuesIdToExportResponse>(`${getBackendUrl()}/issues/getIssuesIdToExport`, data);
}
export async function handleIssuesExportAsPDF({ projectId, issuesIds }: ExportParams): Promise<StatusResponse | void> {
  if (!issuesIds) {
    const res = await getIssuesIdToExport({ projectId });
    if (res.data.status) {
      issuesIds = res.data.result;
    } else {
      return res.data;
    }
  }
  if (issuesIds?.length) {
    exportPdf({ issuesIds, exportType: EXPORT_TYPE.PDF });
  } else {
    return {
      status: false,
      errorMessage: "There are no issues in this Project",
    };
  }
}
export const issuesBcfImport = async <T extends Exact<SaveIssuesDataFromBCFRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<SaveIssuesDataFromBCFResponse>(
    `${getBackendUrl()}/issues/saveIssuesDataFromBCF`,
    data,
  );
  return response;
};
export const getDefaultListOfStatusAvailable = async ({
  spaceId,
  entityType,
}: {
  spaceId: string;
  entityType: string;
}) => {
  const data = {
    value: {
      params: {
        entityType,
        spaceId,
      },
      entityType,
      spaceId,
    },
  };
  return await axiosWrapper.post<FetchDefaultListOfStatusesAvailableResponse>(defaultListOfStatusAvailableUrl, data);
};

/**
 * True when the calling user holds `requiredPermissionId` on this issue.
 *
 * The parameter used to be `unknown` because `userPermissions` — `$addFields`-ed onto every row by
 * `getUserPermissionPipeline` rather than stored — was undeclared on the response contract. It is now
 * `issueItemShape.userPermissions` (`z.array(z.number()).optional()`,
 * server/issues/issues.schema.ts:220), so the parameter can name what it needs instead: every caller
 * passes something that carries that member — the redux slice's `IssueItem`, `LoadedIssue`
 * (`IssueItem & …`, Issuecard.tsx:44) or `IssueGridRow`, which Picks it (Issues.tsx:98) — and the
 * `| undefined` covers the record lookups (`issues[issueId]`) that may miss.
 *
 * The `Array.isArray` guard is kept deliberately: it is the same runtime test as before, so a row
 * whose `userPermissions` is not an array still answers `false` rather than throwing.
 */
export const isIssueRequiredPermission = (
  issuesData: Pick<IssueItem, "userPermissions"> | undefined,
  requiredPermissionId: number,
): boolean => {
  const userPermissions = issuesData?.userPermissions;
  return Array.isArray(userPermissions) && userPermissions.includes(requiredPermissionId);
};
